import { App } from '../App';
import { Application_Constraints, Client_Application } from '../api/typegen';
import {
  getConstraintsDefaultAmountValue,
  getConstraintsDefaultTermValue,
} from 'utils/defaultValues';
import {
  getDefaultCalculatorFromSession,
  SESSION_CALCULATOR_TYPE,
} from 'utils/constantsSession';
import { useGetClientApplication } from '../api/atoms';
import { useMemo } from 'react';
import useClient from '../hooks/useClient';
import useClientConstraints from './useClientConstraints';
import useClientOffer from './useClientOffer';

type PropsAmountTermConstraintsDefault = {
  application?: Client_Application['GET']['Response'] | null;
  constraints?: Application_Constraints['GET']['Response'] | null;
};

export const getAmountAndTermCalculator = ({
  application,
  constraints,
}: PropsAmountTermConstraintsDefault) => {
  const searchParams = new URLSearchParams(window.location.search);
  const amountFromQueryParam =
    searchParams.get('amount') && Number(searchParams.get('amount'));
  const termFromQueryParam =
    searchParams.get('term') && Number(searchParams.get('term'));

  // data from session
  const sessionCalculatorDataParsed: SESSION_CALCULATOR_TYPE | undefined =
    getDefaultCalculatorFromSession();

  // data from Application
  const invalidApplicationResolution = ['CANCELLED', 'REJECTED'];
  const validResolution =
    !application?.resolution ||
    !invalidApplicationResolution.includes(application?.resolution);

  const amountFromApp = validResolution && application?.amount;
  const termFromApp = validResolution && application?.term;

  const defaultAmountValue =
    amountFromQueryParam ||
    sessionCalculatorDataParsed?.amount ||
    amountFromApp ||
    getConstraintsDefaultAmountValue(constraints) ||
    App.config.fallbackAmount;

  const defaultTermValue =
    termFromQueryParam ||
    sessionCalculatorDataParsed?.term ||
    termFromApp ||
    getConstraintsDefaultTermValue(constraints) ||
    App.config.fallbackTerm;

  return {
    amount: defaultAmountValue,
    term: defaultTermValue,
  };
};

export interface ApplicationSummary {
  amount: number;
  loading: boolean;
  term: number;
  interest: number;
  repayableAmount: number;
  repaymentDate: string;
}

export const useApplicationSummary = (config?: {
  term?: number;
}): Partial<ApplicationSummary> => {
  const { data: clientOfferData, fetching: fetchingOffer } = useClientOffer();
  const { fetching: fetchingConstraints, data: constraintsData } =
    useClientConstraints();
  const apiGetClientApplication = useGetClientApplication();
  const { fetching: fetchingClient } = useClient();

  const result = useMemo<Partial<ApplicationSummary>>(
    () => ({
      amount:
        clientOfferData?.newPrincipal || apiGetClientApplication.data?.amount,
      loading:
        fetchingOffer ||
        apiGetClientApplication.fetching ||
        fetchingClient ||
        fetchingConstraints ||
        !constraintsData,
      term:
        clientOfferData?.term ||
        apiGetClientApplication.data?.term ||
        config?.term,
      interest: clientOfferData?.totalInterest,
      repayableAmount:
        clientOfferData?.totalRepayableAmount ||
        (apiGetClientApplication.data?.amount || 0) +
          (clientOfferData?.totalInterest || 0),
      repaymentDate: clientOfferData?.agreementEndDate,
    }),
    [
      apiGetClientApplication.data?.amount,
      apiGetClientApplication.data?.term,
      constraintsData,
      apiGetClientApplication.fetching,
      fetchingOffer,
      clientOfferData?.agreementEndDate,
      clientOfferData?.newPrincipal,
      clientOfferData?.term,
      clientOfferData?.totalInterest,
      clientOfferData?.totalRepayableAmount,
      config?.term,
      fetchingClient,
    ],
  );

  return result;
};
